import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { useSubscriptionStatus } from '../components/SubscriptionGate';
import '../connections.css';

// The connect/sync endpoints 403 when the trial has expired or there's no active
// plan. api.request throws a plain Error carrying the backend `detail` text, so we
// match on that (covers the "trialing" status whose 14-day trial expired by date,
// which the status-string check below can't detect on its own).
function isSubscriptionError(message) {
  const m = (message || '').toLowerCase();
  return m.includes('trial') || m.includes('subscri') || m.includes('upgrade');
}

function ConnectionsPage() {
  const location = useLocation();
  const [business, setBusiness] = useState(null);
  const [user, setUser] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const [success, setSuccess] = useState(location.state?.success || null);

  const fetchData = useCallback(async () => {
    try {
      const [businesses, conns, me] = await Promise.all([
        api.getBusinesses().catch(() => []),
        api.getConnections().catch(() => []),
        api.getMe().catch(() => null),
      ]);
      if (businesses.length > 0) setBusiness(businesses[0]);
      setUser(me);
      setConnections(conns || []);
    } catch (err) {
      console.error('Failed to load connections', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConnect = async () => {
    setError(null);
    setConnecting(true);
    try {
      const { auth_url } = await api.getGoogleAuthUrl();
      // Hand off to Google's consent screen; we return to /settings/google/callback.
      window.location.href = auth_url;
    } catch (err) {
      if (isSubscriptionError(err.message)) {
        setNeedsSubscription(true);
      } else {
        setError(err.message || 'Could not start the Google connection. Please try again.');
      }
      setConnecting(false);
    }
  };

  const handleDisconnect = async (connectionId) => {
    if (!window.confirm('Disconnect Google Business Profile? Automatic review syncing will stop.')) {
      return;
    }
    setError(null);
    try {
      await api.disconnectConnection(connectionId);
      setSuccess('Google Business Profile disconnected.');
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to disconnect. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">Loading connections...</div>
      </div>
    );
  }

  const businessCategory = business?.business_category || 'local_business';
  const isHospitality = businessCategory === 'hospitality';
  const googleConnection = connections.find(
    (c) => c.platform === 'google' && c.is_active
  );
  const { isSubscribed } = useSubscriptionStatus(user);
  // Show the subscribe CTA if the status is clearly unsubscribed, OR if a connect
  // attempt came back with a subscription 403 (the expired-trial-still-"trialing" case).
  const showSubscribeCta = !googleConnection && (!isSubscribed || needsSubscription);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Connections</h1>
        <p className="page-subtitle">
          Connect your review platform to sync reviews automatically. You can also add
          reviews manually from the Reviews page at any time.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <section className="connections-section">
        <h2>Review Platforms</h2>

        <div className="platform-cards">
          {isHospitality ? (
            // Airbnb integration is not available yet.
            <div className="platform-card coming-soon">
              <div className="platform-card-header">
                <div className="platform-icon airbnb-icon">A</div>
                <div className="platform-info">
                  <h3>Airbnb</h3>
                  <span className="connection-status coming-soon-badge">Coming Soon</span>
                </div>
              </div>
              <div className="platform-card-details">
                <p className="platform-description">
                  Automatic Airbnb review syncing is coming soon. For now, copy guest
                  reviews from your Airbnb dashboard and add them manually.
                </p>
              </div>
              <div className="platform-card-actions">
                <a href="/reviews" className="btn btn-outline">Go to Reviews</a>
              </div>
            </div>
          ) : (
            <div className={`platform-card ${googleConnection ? 'connected' : ''}`}>
              <div className="platform-card-header">
                <div className="platform-icon google-icon">G</div>
                <div className="platform-info">
                  <h3>Google Business Profile</h3>
                  {googleConnection ? (
                    <span className="connection-status connected">Connected</span>
                  ) : (
                    <span className="connection-status disconnected">Not connected</span>
                  )}
                </div>
              </div>

              {googleConnection ? (
                <div className="platform-card-details">
                  <div className="detail-row">
                    <span className="detail-label">Location</span>
                    <span className="detail-value">
                      {googleConnection.location_name || 'Unknown'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Last synced</span>
                    <span className="detail-value">
                      {googleConnection.last_polled_at
                        ? new Date(googleConnection.last_polled_at).toLocaleString('en-GB')
                        : 'Not yet'}
                    </span>
                  </div>
                </div>
              ) : showSubscribeCta ? (
                <div className="platform-card-details">
                  <p className="platform-description">
                    🔒 Connecting Google Business Profile requires an active subscription.
                    Your free trial has ended — subscribe to sync and reply to your reviews.
                  </p>
                </div>
              ) : (
                <div className="platform-card-details">
                  <p className="platform-description">
                    Connect your Google Business Profile to automatically pull in new
                    reviews and post your approved responses back to Google.
                  </p>
                </div>
              )}

              <div className="platform-card-actions">
                {googleConnection ? (
                  <button
                    className="btn btn-outline btn-danger"
                    onClick={() => handleDisconnect(googleConnection.id)}
                  >
                    Disconnect
                  </button>
                ) : showSubscribeCta ? (
                  <a href="/billing" className="btn btn-primary" style={{ textDecoration: 'none' }}>
                    Subscribe to connect
                  </a>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={handleConnect}
                    disabled={connecting}
                  >
                    {connecting ? 'Connecting…' : 'Connect Google Business Profile'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default ConnectionsPage;
